import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, FileText, Stethoscope, Clock, Phone, Loader2, Check, ChevronDown } from "lucide-react";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";
import { toast } from "sonner";

/* Google Ads iniş sayfası — tek amaç: ücretsiz ön değerlendirme başvurusu. TR, mobil öncelikli, dikkat dağıtıcı menü yok. */

const WA = "https://wa.me/905383085160?text=Merhaba%2C%20%C3%BCcretsiz%20%C3%B6n%20de%C4%9Ferlendirme%20i%C3%A7in%20yaz%C4%B1yorum.";

const TREATMENTS = ["Henüz tanı aşamasındayım", "Proton Terapisi", "BNCT", "Ağır İyon (Karbon)", "CAR-T Hücre Terapisi", "iPSC / Yenileyici Tıp", "Kişiye Özel Kanser Aşısı", "Emin değilim — yönlendirme istiyorum"];

const STEPS = [
  { icon: FileText, title: "Dosyanızı gönderin", text: "Formu doldurun; raporlarınızı sonra da ekleyebilirsiniz." },
  { icon: Stethoscope, title: "Uzman kurul inceler", text: "Dosyanız Japonya'daki akredite merkezin uzman kuruluna iletilir." },
  { icon: Clock, title: "Size dönelim", text: "Uygunluk ön değerlendirmesini ücretsiz olarak paylaşırız." },
];

const FAQ = [
  { q: "Ön değerlendirme ücretli mi?", a: "Hayır. Dosyanızın Japonya'daki merkez tarafından ön incelemesi tamamen ücretsizdir; tedavi kararı ve teklif ancak siz onaylarsanız sonraki adımda netleşir." },
  { q: "Her hasta kabul ediliyor mu?", a: "Hayır. Uygunluk her zaman Japon merkezin uzman kurulunun kararıdır; biz dosyanızı en doğru merkeze ulaştırırız." },
  { q: "Vize ve konaklama nasıl oluyor?", a: "I-Cell Networks akreditasyonuyla tıbbi kalış vizesi, tercümanlık, konaklama ve yaşam desteği uçtan uca koordine edilir." },
];

export default function AdsLanding() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", phone: "", email: "", treatment: "", message: "" });
  const [kvkk, setKvkk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!kvkk) return toast.error("Devam etmek için KVKK onayı gerekli.");
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Ad ve telefon zorunludur.");
    setBusy(true);
    try {
      await api.post("/applications", {
        name: form.name.trim(),
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
        phone: form.phone.trim(),
        treatment: form.treatment,
        message: form.message.trim(),
        kind: "reklam_inis",
        source: [params.get("utm_source"), params.get("utm_medium"), params.get("utm_campaign"), params.get("gclid") ? "gclid" : null].filter(Boolean).join("|"),
        consent_kvkk: true,
      });
      setDone(true);
      // Google Ads/GTM dönüşüm ölçümü — GTM'de "application_submitted" tetikleyicisiyle Ads dönüşümüne bağlanır
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "application_submitted", kind: "reklam_inis", treatment: form.treatment });
      if (window.gtag) window.gtag("event", "generate_lead", { event_category: "ads_landing", event_label: form.treatment || "genel" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Gönderilemedi — lütfen WhatsApp hattını deneyin.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full h-12 px-4 rounded-xl border border-black/10 bg-white text-sm text-ink placeholder:text-sub/60 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition";

  return (
    <div className="min-h-screen bg-[#FAF6EE]" data-testid="ads-landing">
      <SEO title="Ücretsiz Ön Değerlendirme — Japonya'da İleri Kanser Tedavisi" description="Dosyanızı Japonya'daki uzman kurul ücretsiz ön değerlendirsin. CAR-T, BNCT, proton, ağır iyon ve kişiye özel aşı seçenekleri." path="/basvuru" />

      {/* minimal header */}
      <header className="bg-[#16040a] py-4 px-6 flex items-center justify-between">
        <div className="font-serif text-lg font-bold text-[#F5EFE6]">2D <span className="text-[#C9A96A]">Healthcare</span></div>
        <a data-testid="ads-call" href="tel:+905383085160" className="flex items-center gap-2 text-[#C9A96A] text-sm font-semibold"><Phone size={15} /> +90 538 308 51 60</a>
      </header>

      {done ? (
        <section className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="ads-success">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
            <Check size={36} className="text-emerald-600" />
          </motion.div>
          <h1 className="mt-6 font-serif text-3xl sm:text-4xl font-bold text-ink">Başvurunuz alındı</h1>
          <p className="mt-4 text-sub leading-relaxed">Teşekkürler. Koordinatörümüz en kısa sürede sizi arayacak. Dilerseniz beklemeden WhatsApp hattımızdan da yazabilirsiniz.</p>
          <a data-testid="ads-success-wa" href={WA} target="_blank" rel="noreferrer" className="mt-8 inline-flex items-center gap-2.5 h-12 px-8 rounded-full bg-[#25D366] text-white font-bold text-sm hover:brightness-110 transition">
            WhatsApp'tan Yazın
          </a>
        </section>
      ) : (
        <>
          {/* hero */}
          <section className="relative overflow-hidden bg-[linear-gradient(160deg,#16040a_0%,#38101f_55%,#16040a_100%)] text-center px-6 pt-16 pb-20">
            <span aria-hidden className="absolute left-1/2 top-0 -translate-x-1/2 font-magserif text-[40vw] leading-none text-[#C9A96A] opacity-[0.06] select-none pointer-events-none">命</span>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="relative max-w-3xl mx-auto">
              <p className="text-[11px] font-semibold tracking-[0.35em] text-[#C9A96A] uppercase">I-Cell Networks Japonya · Türkiye Temsilcisi</p>
              <h1 className="mt-5 font-serif text-4xl sm:text-5xl font-bold tracking-tight text-[#F5EFE6] text-balance">
                Dosyanızı Japonya'daki uzman kurul <span className="text-[#C9A96A]">ücretsiz</span> ön değerlendirsin
              </h1>
              <p className="mt-5 text-[#E8DCC8]/75 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
                CAR-T, BNCT, ağır iyon, proton ve kişiye özel kanser aşısı — hangisinin sizin için uygun olduğunu birlikte netleştirelim.
              </p>
              <a data-testid="ads-hero-cta" href="#basvuru-formu" className="mt-8 inline-flex items-center gap-2.5 h-14 px-10 rounded-full bg-[#C9A96A] text-[#1d0810] font-heading font-bold text-base tracking-wide hover:bg-[#dfc08a] transition-colors shadow-[0_16px_40px_-12px_rgba(201,169,106,0.6)]">
                Ücretsiz Ön Değerlendirme Alın
              </a>
              <p className="mt-4 text-[11px] text-[#E8DCC8]/50">Yükümlülük yok · Fiyat verilmez · Uygunluk uzman kurul kararıdır</p>
            </motion.div>
          </section>

          {/* doktor guven seridi */}
          <section className="max-w-3xl mx-auto px-6 -mt-10 relative z-10">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-3xl border border-black/5 shadow-xl p-6 flex items-center gap-5">
              <img src="/img/dr-akdemir.jpg" alt="Dr. Mehmet Akdemir" className="w-20 h-20 rounded-2xl object-cover object-top shrink-0" />
              <div>
                <div className="font-serif text-lg font-bold text-ink">Dr. Mehmet Akdemir</div>
                <p className="text-xs text-sub leading-relaxed mt-1">Anesteziyoloji ve Reanimasyon uzmanı · CybernicX (Cyber Robotic Center) kurucusu · CYBERDYNE Inc. ortağı · "N-of-1" kişiye özel tıp vizyonu</p>
              </div>
            </motion.div>
          </section>

          {/* adimlar */}
          <section className="max-w-4xl mx-auto px-6 py-16 grid sm:grid-cols-3 gap-5">
            {STEPS.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white rounded-2xl border border-black/5 p-6">
                <div className="w-11 h-11 rounded-full bg-brand/10 flex items-center justify-center"><s.icon size={19} className="text-brand" /></div>
                <div className="mt-4 font-heading font-bold text-ink">{i + 1}. {s.title}</div>
                <p className="mt-2 text-sm text-sub leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </section>

          {/* form */}
          <section id="basvuru-formu" className="max-w-xl mx-auto px-6 pb-20">
            <motion.form onSubmit={submit} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-3xl border border-black/5 shadow-xl p-7" data-testid="ads-form">
              <h2 className="font-serif text-2xl font-bold text-ink text-center">Ücretsiz Ön Değerlendirme</h2>
              <p className="mt-2 text-sm text-sub text-center">2 dakikanızı alır. Koordinatörümüz sizi arar.</p>
              <div className="mt-6 space-y-3.5">
                <input data-testid="ads-name" required placeholder="Ad Soyad *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                <input data-testid="ads-phone" required type="tel" placeholder="Telefon (WhatsApp) *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                <input data-testid="ads-email" type="email" placeholder="E-posta (isteğe bağlı)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                <div className="relative">
                  <select data-testid="ads-treatment" value={form.treatment} onChange={(e) => setForm({ ...form, treatment: e.target.value })} className={inputCls + " appearance-none pr-10"}>
                    <option value="">İlgilendiğiniz tedavi (isteğe bağlı)</option>
                    {TREATMENTS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-sub pointer-events-none" />
                </div>
                <textarea data-testid="ads-message" rows={3} placeholder="Kısaca durumu anlatın (tanı, evre, mevcut tedavi...)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-black/10 bg-white text-sm text-ink placeholder:text-sub/60 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition resize-none" />
                <label className="flex items-start gap-2.5 text-xs text-sub leading-relaxed cursor-pointer">
                  <input data-testid="ads-kvkk" type="checkbox" checked={kvkk} onChange={(e) => setKvkk(e.target.checked)} className="mt-0.5 w-4 h-4 rounded accent-[#7B1E2B]" />
                  <span>KVKK kapsamında bilgilerimin ön değerlendirme amacıyla işlenmesini ve Japonya'daki merkezle paylaşılmasını kabul ediyorum.</span>
                </label>
                <button data-testid="ads-form-submit-button" type="submit" disabled={busy} className="w-full h-14 rounded-full bg-brand text-white font-heading font-bold text-base hover:bg-branddeep disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
                  {busy ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />} {busy ? "Gönderiliyor…" : "Ücretsiz Başvuruyu Gönder"}
                </button>
                <a data-testid="ads-whatsapp" href={WA} target="_blank" rel="noreferrer" className="w-full h-12 rounded-full border-2 border-[#25D366]/60 text-[#128C4B] font-bold text-sm hover:bg-[#25D366]/10 transition-colors flex items-center justify-center gap-2">
                  veya WhatsApp'tan Yazın
                </a>
              </div>
            </motion.form>
          </section>

          {/* mini sss */}
          <section className="max-w-xl mx-auto px-6 pb-20 space-y-3">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl border border-black/5 overflow-hidden">
                <button data-testid={`ads-faq-${i}`} onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-5 py-4 text-left">
                  <span className="font-heading font-semibold text-sm text-ink">{f.q}</span>
                  <ChevronDown size={16} className={`text-sub transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="px-5 pb-4 text-sm text-sub leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </section>
        </>
      )}

      <footer className="bg-[#16040a] py-6 text-center text-[11px] text-white/40 px-6">
        © 2026 2D Healthcare (Diagenics Ar-Ge Ltd. Şti.) · I-Cell Networks Japonya Türkiye Temsilcisi · Bu sayfa bilgilendirme amaçlıdır; tıbbi tavsiye içermez.
      </footer>
    </div>
  );
}
